import * as React from 'react';
import {createViewConfig, view, assignProps} from '@sail/engine';
import {css} from '../css';
import type {View} from '../view';
import {useCurrentLayer} from './layers/Layer';

const isNestedOverlay = (
  layerContext: ReturnType<typeof useCurrentLayer> | null | undefined,
): boolean => {
  if (!layerContext) {
    return false;
  }

  if (layerContext.type === 'overlay') {
    return true;
  }

  return isNestedOverlay(layerContext.parent);
};

export type BackdropProps = View.IntrinsicElement<
  'div',
  {
    onClose?: () => void;
    /**
     * @deprecated Use onClose instead of onPress
     * @ignore
     */
    onPress?: () => void;

    /**
     * @private
     *
     * The opacity of the backdrop.
     */
    opacity?: number;
  }
>;

export const BackdropConfig = createViewConfig({
  props: {} as BackdropProps,
  name: 'Backdrop',
  flattens: true,
});

function useBackdropOpacity(
  layerContext: ReturnType<typeof useCurrentLayer>,
): number | undefined {
  // For stacked Drawers, set opacity to 0 when in a nested layer
  if (isNestedOverlay(layerContext?.parent)) {
    return 0;
  }

  // By default, the opacity is undefined (will be set by parent component)
  return undefined;
}

export const Backdrop = BackdropConfig.createView(
  ({onClose, onPress, opacity, ...props}) => {
    const layerContext = useCurrentLayer();
    const ref = React.useRef<HTMLDivElement>(null);
    // Dialog and BottomSheet pass their own opacity. defaultOpacity is used for Drawers and any regular Backdrop usages.
    const defaultOpacity = useBackdropOpacity(layerContext);
    const mountTimeRef = React.useRef<number>(0);

    React.useEffect(() => {
      mountTimeRef.current = Date.now();
    }, []);

    return (
      <view.div
        data-testid="backdrop"
        ref={ref}
        uses={[
          assignProps({
            onClick: (event) => {
              const elapsedTime = Date.now() - mountTimeRef.current;

              // Do not close the backdrop if the user clicks on a child element or if the backdrop has been mounted for less than 100ms
              if (
                event.target === event.currentTarget &&
                (process.env.NODE_ENV === 'test' ||
                  (process.env.NODE_ENV !== 'test' && elapsedTime > 100))
              ) {
                (onClose ?? onPress)?.();
              }
            },
          }),
        ]}
        css={[
          css({opacity: opacity ?? defaultOpacity}),
          // Backdrop in touch devices should never be scrollable, we set `touch-action: none` to prevent tap+drag in the Backdrop to scroll the document's body in iOS.
          // This has no effect when enableBottomSheeets is true, since we already have a mechanism in RootLayer to lock body scroll in iOS. Setting touch-action here
          // still prevents issues when `enableBottomSheets` is not enabled, the deprecated RootLayer is used, or the components are rendered within iframes (Connect).
          // For safety, we only set touch-action to none when the Backdrop has no children, since it is not scrollable in that case, and there are a few cases where
          // the Backdrop is used as a wrapper for other components.
          props.children
            ? css({touchAction: 'unset'})
            : css({touchAction: 'none'}),
        ]}
        {...props}
      />
    );
  },
  {
    css: {
      backgroundColor: 'backdrop',
      position: 'fixed',
      top: 'space.0',
      left: 'space.0',
      right: 'space.0',
      bottom: 'space.0',
    },
  },
);
